import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';
import { MatOption, MatSelect, MatSelectChange } from '@angular/material/select';
import { BrandingField, defaultBrandingSettings } from '@app/core/models/branding.models';
import { SnackBarType } from '@app/core/models/snack-bar-data.model';
import { AppTranslateService } from '@app/core/services/app-translate.service';
import { BrandingService } from '@app/core/services/branding.service';
import { SnackbarService } from '@app/core/services/snack-bar.service';
import { RecordingAnnouncementService } from '@app/phone/services/recording-announcement.service';
import {
  DefaultRecordingAnnouncementSettings,
  RecordingAnnouncementSettings,
  RecordingAnnouncementType,
} from '@app/preferences/models/recording-announcements.models';
import { VoiceOptions } from '@app/preferences/models/settings.models';
import { SettingsPage } from '@app/preferences/models/settings-page';
import { SettingsService } from '@app/preferences/services/settings.service';
import { BrandingColorDirective } from '@app/shared/directives/branding-color.directive';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateModule } from '@ngx-translate/core';
import { combineLatest } from 'rxjs';

@UntilDestroy()
@Component({
  selector: 'app-call-recordings',
  templateUrl: './call-recordings.component.html',
  styleUrls: ['./call-recordings.component.scss'],
  imports: [
    CommonModule,
    MatFormField,
    MatLabel,
    MatSelect,
    MatOption,
    MatInput,
    ReactiveFormsModule,
    TranslateModule,
    MatRadioGroup,
    MatRadioButton,
    FormsModule,
    MatIcon,
    MatProgressSpinner,
    MatSuffix,
    BrandingColorDirective,
  ],
  standalone: true,
})
export class CallRecordingsComponent extends SettingsPage implements OnInit, AfterViewInit {
  @ViewChild('fileInput') fileInput: ElementRef<HTMLInputElement>;

  protected type: RecordingAnnouncementType = 'tts';

  protected readonly ttsAnnouncements: Array<{ text: string; modified: boolean; blob: Blob | undefined }> = [];
  protected readonly fileAnnouncements: Array<{ modified: boolean; blob: Blob | undefined }> = [];
  protected readonly states: Array<{ inProgress: boolean; playing: boolean }> = [];

  protected ttsForm: FormGroup;
  protected filesForm: FormGroup;

  protected voices: VoiceOptions[] = [];

  protected readonly BrandingField = BrandingField;
  protected defaultBrandingSettings = defaultBrandingSettings;

  protected readonly DefaultRecordingAnnouncementSettings = DefaultRecordingAnnouncementSettings;

  protected get language() {
    return this.ttsForm.get('language');
  }
  protected get voice() {
    return this.ttsForm.get('voice');
  }
  protected get ttsMessages() {
    return this.ttsForm.controls['messages'] as FormArray;
  }
  protected get files() {
    return this.filesForm.controls['files'] as FormArray;
  }

  private get defaultLanguage() {
    return DefaultRecordingAnnouncementSettings.ttsLanguage;
  }
  private get defaultVoice() {
    return DefaultRecordingAnnouncementSettings.voice;
  }

  constructor(
    protected recAnnouncementService: RecordingAnnouncementService,
    private fb: FormBuilder,
    private settingsService: SettingsService,
    private snackbar: SnackbarService,
    private translations: AppTranslateService,
    protected brandingService: BrandingService
  ) {
    super();

    this.ttsForm = this.fb.group({
      language: [this.defaultLanguage, [Validators.required, Validators.minLength(2)]],
      voice: [this.defaultVoice, [Validators.required, Validators.minLength(2)]],
      messages: [this.fb.array([]), [Validators.required, this.arrayValidator()]],
    });

    this.filesForm = this.fb.group({
      files: [this.fb.array([]), [Validators.required]],
    });

    this.recAnnouncementService.filteredVoicesByLanguage$.pipe(untilDestroyed(this)).subscribe((voices) => {
      this.voices = voices;
    });

    for (let i = 0; i < 4; i++) {
      this.ttsMessages.value.push(new FormControl('', [Validators.required, Validators.minLength(2)]));
      this.files.value.push(new FormControl('', [Validators.required, Validators.minLength(1)]));
      this.states.push({ inProgress: false, playing: false });
      this.ttsAnnouncements.push({ text: '', modified: false, blob: undefined });
      this.fileAnnouncements.push({ modified: false, blob: undefined });
    }
  }

  ngOnInit(): void {
    this.recAnnouncementService.announcementSettings$.pipe(untilDestroyed(this)).subscribe(async (settings) => {
      this.type = settings.announcementType;

      this.language?.patchValue(settings.ttsLanguage);
      this.voice?.patchValue(settings.voice);

      settings.ttsMessages.forEach((message, index) => {
        const newMessage = this.translations.instant(message);
        const ctrl = this.ttsMessages.value.at(index);
        ctrl.patchValue(newMessage);
        ctrl.markAsUntouched();
        this.ttsAnnouncements[index].text = newMessage;
      });

      this.recAnnouncementService.filterVoicesByLanguage(settings.ttsLanguage).then(() => {
        this.ttsForm.updateValueAndValidity();
        this.ttsForm.markAsUntouched();
      });

      if (settings.announcementType === 'files') {
        await Promise.all(
          this.fileAnnouncements.map(async (announcement, index) => {
            announcement.blob = await this.recAnnouncementService.getRecordingAnnouncement(index);
            const ctrl = this.files.value.at(index);
            ctrl.patchValue('file'); // just need to have some non-empty value there
          })
        );
      }

      this.ttsMessages.markAsUntouched();
      this.ttsForm.updateValueAndValidity();
      this.files.updateValueAndValidity();
    });
  }

  ngAfterViewInit(): void {
    combineLatest([this.ttsForm.statusChanges, this.files.valueChanges])
      .pipe(untilDestroyed(this))
      .subscribe(([status, control]) => {
        if (!this.loading) {
          this.disabled =
            this.type === 'tts' ? !this.ttsForm.touched || status === 'INVALID' : !control.touched || control.invalid;
        }
      });
  }

  protected handleTypeChange() {
    // clear values on method change, forces user to enter values
    if (this.type === 'tts') {
      this.language?.patchValue(this.defaultLanguage);
      this.voice?.patchValue(this.defaultVoice);
      DefaultRecordingAnnouncementSettings.ttsMessages.forEach((message, index) => {
        this.ttsMessages.value.at(index).patchValue(this.translations.instant(message));
        this.ttsAnnouncements[index].text = '';
      });
      this.recAnnouncementService.filterVoicesByLanguage(this.defaultLanguage);
      this.ttsForm.markAsTouched();
      this.ttsForm.updateValueAndValidity();
    } else {
      [0, 1, 2, 3].forEach((index) => {
        this.fileAnnouncements[index] = { modified: true, blob: undefined };
        this.files.value.at(index).patchValue('');
      });
      this.files.updateValueAndValidity();
    }
  }

  protected handleLanguageChange(event: MatSelectChange) {
    this.recAnnouncementService.filterVoicesByLanguage(event.value);
    this.voice?.patchValue('');
    this.clearTTSAnnouncements();
  }

  protected handleVoiceChange() {
    this.ttsForm.markAsTouched();
    this.ttsForm.updateValueAndValidity();
    this.clearTTSAnnouncements();
  }

  protected async handleTTSPlay(index: number) {
    try {
      this.states[index].inProgress = true;
      await this.createAnnouncement(index);
      this.states[index].inProgress = false;

      this.playAnnouncement(index, this.ttsAnnouncements[index].blob!);
    } catch (error) {
      console.error('Error while creating announcement:', error);
      this.snackbar.open(this.translations.instant('CALL_RECORDINGS_PREFERENCES.CREATE_ERROR'), undefined, {
        duration: 5000,
        panelClass: [SnackBarType.WARNING],
      });
    } finally {
      this.states[index].inProgress = false;
    }
  }

  protected handleMessageChange() {
    this.ttsMessages.markAsTouched();
    this.ttsMessages.updateValueAndValidity();
  }

  protected handlePlay(index: number) {
    const blob = this.fileAnnouncements[index].blob;
    if (blob) {
      this.playAnnouncement(index, blob);
    }
  }

  protected handleUpload(index: number) {
    // index is stored in element id
    this.fileInput.nativeElement.id = String(index);
    this.fileInput.nativeElement.click();
  }

  protected async handleFileInput(event: Event) {
    const target = event.target as HTMLInputElement;
    const index = Number(target.id);

    if (target.files) {
      const file = target.files[0];
      this.fileAnnouncements[index].blob = file;
      this.fileAnnouncements[index].modified = true;

      this.files.value.at(index).patchValue(file.name);
      this.files.value.at(index).markAsTouched();
      this.files.updateValueAndValidity();
    }

    target.value = '';
  }

  public async save() {
    if (this.loading || this.disabled) {
      return Promise.reject();
    }

    try {
      // loading and disabled don't work visually well together so using only loading
      this.loading = true;

      const formData = this.ttsForm.value;
      const settings: RecordingAnnouncementSettings = {
        announcementType: 'tts',
        ttsLanguage: formData.language,
        voice: formData.voice,
        ttsMessages: this.ttsMessages.value.value,
      };

      let announcements: Array<{ modified: boolean; blob: Blob | undefined }>;
      const announcementsToUpload: Array<{ index: number; announcement: Blob }> = [];

      if (this.type === 'tts') {
        settings.announcementType = 'tts';

        await Promise.all([0, 1, 2, 3].map((index) => this.createAnnouncement(index)));
        announcements = this.ttsAnnouncements;
      } else {
        settings.announcementType = 'files';
        announcements = this.fileAnnouncements;
      }

      announcements.forEach((announcement, index) => {
        if (announcement.modified) {
          announcementsToUpload.push({ index, announcement: announcement.blob! });
        }
      });

      await this.recAnnouncementService.uploadRecordingAnnouncements(announcementsToUpload);

      // saving settings in the last step
      // otherwise subscription to settings would cause message fields overwritting
      await this.recAnnouncementService.saveSettings(settings);
    } catch (error) {
      console.error('Error while saving announcement settings:', error);
      this.snackbar.open(this.translations.instant('CALL_RECORDINGS_PREFERENCES.SAVE_ERROR'), undefined, {
        duration: 5000,
        panelClass: [SnackBarType.WARNING],
      });
      return Promise.reject(); // do not return to profile tab
    } finally {
      this.loading = false;
    }
  }

  private async createAnnouncement(index: number): Promise<void> {
    return new Promise(async (resolve, reject) => {
      const announcement = this.ttsAnnouncements[index];

      if (announcement.blob === undefined && !announcement.modified) {
        // empty blob and not modified => it should be stored in db
        announcement.blob = await this.recAnnouncementService.getRecordingAnnouncement(index);
      }

      const text = this.ttsMessages.value.at(index).value;

      if (announcement.text !== text || announcement.blob === undefined) {
        const filteredVoice = this.voices.filter((voice) => voice.id === this.ttsForm.get('voice')?.value);

        if (filteredVoice.length === 1) {
          try {
            const blob = (await this.recAnnouncementService.getVoicePreview(text, filteredVoice[0])) || undefined;

            this.ttsAnnouncements[index] = { text, blob, modified: true };
          } catch (error) {
            console.error('Error while creating announcement:', error);
            this.ttsAnnouncements[index] = { text: '', blob: undefined, modified: true };
            reject(error);
          }
        }
      }
      resolve();
    });
  }

  private playAnnouncement(index: number, blob: Blob) {
    this.states[index].playing = true;

    const url = URL.createObjectURL(blob);

    const audio = new Audio();
    audio.setSinkId(this.settingsService.getSpeaker());
    audio.src = url;
    audio.addEventListener('ended', () => {
      this.states[index].playing = false;
      URL.revokeObjectURL(url);
    });

    audio.play();
  }

  private clearTTSAnnouncements() {
    this.ttsAnnouncements.forEach((announcement) => {
      announcement.text = '';
      announcement.blob = undefined;
      announcement.modified = true;
    });
  }

  private arrayValidator(): ValidatorFn | null {
    return (control: AbstractControl): ValidationErrors | null => {
      const controls = control.value as FormArray;

      for (let i = 0; i < controls.length; i++) {
        if (controls.at(i).value.length === 0) {
          return {
            emptyMessageError: this.translations.instant('CALL_RECORDINGS_PREFERENCES.EMPTY_ANNOUNCEMENT_ERROR'),
          };
        }
      }

      return null;
    };
  }
}
